import { useContext, type ReactNode } from "react";
import classNames from "classnames";
import { AlertCircleIcon, CheckIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { Loader } from "@design-system/Loader/Loader";
import {
    AccrualReviewStatus,
    ImporterErrors,
} from "@common/types/financialDocument";
import { ImporterDisplayErrorKind } from "@components/importer/helpers";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import { AccrualImporterContext } from "./accrualImporter.context";
import { AccrualImporterDocumentRow } from "./accrualImporter.types";
import {
    AccrualDocumentImportStatus,
    getAccrualDocumentImportStatus,
} from "./accrualImporter.helpers";
import { accrualDraftCopy } from "./draftDetails/accrualDraftCopy";
import styles from "./AccrualImporterDocumentStatus.module.scss";

interface Props {
    documentRow: AccrualImporterDocumentRow;
}

interface DraftStatusLabelParams {
    documentRow: AccrualImporterDocumentRow;
    importStatus: AccrualDocumentImportStatus;
    readyToImportLabel: string;
    mode: AccrualDocumentImporterMode;
}

function getDraftStatusLabel({
    documentRow,
    importStatus,
    readyToImportLabel,
    mode,
}: DraftStatusLabelParams): string {
    switch (importStatus.kind) {
        case "uploading":
            return "Uploading";
        case "processing":
            return "Extracting";
        case "imported":
            return "Imported";
        case "import_scheduled":
            return "Import scheduled";
        case "importing":
            return "Importing";
        case "import_failed_retryable":
            return "Ready to import (last import failed!)";
        case "extraction_failed":
        case "review_tasks":
        case "ready_with_warnings": {
            const labels = accrualDraftCopy(mode).reviewStatusLabels;
            const accrualReviewStatus =
                documentRow.financialDocument?.accrualReviewStatus;
            if (accrualReviewStatus) {
                return labels[accrualReviewStatus];
            }
            if (
                importStatus.kind !== "extraction_failed" &&
                importStatus.errors.some(
                    (displayError) =>
                        displayError.kind ===
                            ImporterDisplayErrorKind.IMPORTER_EXTRACTION &&
                        displayError.error ===
                            ImporterErrors.ACCRUAL_UNCONFIRMED_ACCOUNT,
                )
            ) {
                return labels[AccrualReviewStatus.CONFIRM_ACCOUNTS];
            }
            return "Ready to review";
        }
        case "ready":
            return readyToImportLabel;
        default: {
            const unreachableStatus: never = importStatus;
            return unreachableStatus;
        }
    }
}

function getStatusIcon(kind: AccrualDocumentImportStatus["kind"]): ReactNode {
    switch (kind) {
        case "extraction_failed":
        case "review_tasks":
        case "ready_with_warnings":
            return <AlertCircleIcon aria-hidden />;
        case "ready":
        case "import_failed_retryable":
        case "imported":
            return <CheckIcon aria-hidden />;
        case "uploading":
        case "processing":
        case "import_scheduled":
        case "importing":
            return <Loader inline className={styles.statusLoader} />;
        default: {
            const unreachableKind: never = kind;
            return unreachableKind;
        }
    }
}

function isAccrualImportIssue(
    kind: AccrualDocumentImportStatus["kind"],
): boolean {
    return (
        kind === "extraction_failed" ||
        kind === "review_tasks" ||
        kind === "ready_with_warnings"
    );
}

export const AccrualImporterDocumentStatus: React.FC<Props> = ({
    documentRow,
}) => {
    const { mode, readyToImportLabel } = useContext(AccrualImporterContext);
    const importStatus = getAccrualDocumentImportStatus(documentRow);
    const label = getDraftStatusLabel({
        documentRow,
        importStatus,
        readyToImportLabel,
        mode,
    });

    return (
        <HStack
            gap={6}
            align="center"
            className={classNames(styles.status, {
                [styles.statusIssue]: isAccrualImportIssue(importStatus.kind),
            })}
        >
            {getStatusIcon(importStatus.kind)}
            <span>{label}</span>
        </HStack>
    );
};
